<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import type { MediaAsset, Product } from '@bf/domain'
import { useShowroomStore } from '../store/showroom'
import EngineerSupplementPanel from '../components/EngineerSupplementPanel.vue'

type TerminalFilter = 'all' | 'sensor' | 'switch' | 'actuator' | 'meter' | 'hmi' | 'accessory'

const store = useShowroomStore()
const filter = ref<TerminalFilter>('all')
const query = ref('')
const selectedId = ref('')
const detailDevice = ref<Product | null>(null)
const filters: Array<{ id: TerminalFilter; label: string }> = [
  { id: 'all', label: '全部设备' },
  { id: 'sensor', label: '传感器' },
  { id: 'switch', label: '状态开关' },
  { id: 'actuator', label: '执行器' },
  { id: 'meter', label: '计量仪表' },
  { id: 'hmi', label: '控制面板' }
  ,{ id: 'accessory', label: '配件与电源' }
]

const allDevices = computed(() =>
  store.entities.value
    .filter(
      (entity): entity is Product =>
        entity.contentType === 'product' &&
        entity.productCategory === 'terminal-device' &&
        entity.tags.includes('terminal-item')
    )
    .sort((left, right) => left.sortOrder - right.sortOrder)
)
const devices = computed(() =>
  allDevices.value.filter(
    (device) =>
      (filter.value === 'all' || device.tags.includes(filter.value)) &&
      (!query.value.trim() || `${device.title} ${device.model}`.toLowerCase().includes(query.value.trim().toLowerCase()))
  )
)
const selectedDevice = computed(
  () =>
    devices.value.find((device) => device.id === selectedId.value) ??
    devices.value[0] ??
    null
)

watch(
  devices,
  (items) => {
    if (!items.some((item) => item.id === selectedId.value)) {
      selectedId.value = items[0]?.id ?? ''
    }
  },
  { immediate: true }
)

function coverAsset(device: Product): MediaAsset | undefined {
  return device.mediaIds
    .map((id) => store.getAsset(id))
    .find((asset): asset is MediaAsset => asset?.mediaType === 'image')
}

function manualAsset(device: Product): MediaAsset | undefined {
  return device.mediaIds
    .map((id) => store.getAsset(id))
    .find((asset): asset is MediaAsset => asset?.mediaType === 'document')
}

function openDetails(device: Product) {
  selectedId.value = device.id
  detailDevice.value = device
}

function closeDetails() {
  detailDevice.value = null
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') closeDetails()
}

onMounted(() => window.addEventListener('keydown', handleKeydown))
onBeforeUnmount(() => window.removeEventListener('keydown', handleKeydown))
</script>

<template>
  <section class="reference-page-heading">
    <p class="eyebrow">FIELD DEVICES</p>
    <h2>楼宇控制末端设备</h2>
    <p>采用源创智控经销商产品目录的实物图、型号和规格，展示现场设备如何接入DDC控制系统。</p>
  </section>

  <div class="terminal-filter-bar" role="group" aria-label="末端设备筛选">
    <button
      v-for="item in filters"
      :key="item.id"
      type="button"
      :class="{ active: filter === item.id }"
      :aria-pressed="filter === item.id"
      @click="filter = item.id"
    >
      {{ item.label }}
    </button>
    <label class="terminal-search"><span>搜索</span><input v-model="query" type="search" placeholder="输入设备名称或型号" /></label>
    <span>网站同步 {{ allDevices.length }} 件 · 当前显示 {{ devices.length }} 件</span>
    <RouterLink class="button button-ghost" :to="{ name: 'catalog', params: { section: 'hardware' } }">硬件产品库</RouterLink>
  </div>

  <div class="terminal-showcase-layout">
    <section class="terminal-device-grid">
      <button
        v-for="device in devices"
        :key="device.id"
        type="button"
        class="terminal-device-card"
        :class="{ active: selectedDevice?.id === device.id }"
        :aria-label="`查看${device.title}主要参数`"
        @click="selectedId = device.id"
      >
        <span class="terminal-image">
          <img
            v-if="coverAsset(device)"
            :src="store.mediaUrl(coverAsset(device)!)"
            :alt="coverAsset(device)!.altText"
            loading="lazy"
          />
        </span>
        <strong>{{ device.title }}</strong>
        <small>{{ device.summary }}</small>
        <span class="terminal-tags">
          <i v-for="tag in device.tags.slice(1, 3)" :key="tag">{{ tag }}</i>
        </span>
        <span class="terminal-open-hint">点击查看主要参数 <b>→</b></span>
      </button>
    </section>

    <aside v-if="selectedDevice" class="terminal-side-panel">
      <section class="terminal-current-device">
        <span>当前设备</span>
        <h3>{{ selectedDevice.title }}</h3>
        <p>{{ selectedDevice.model }}</p>
        <dl>
          <div v-for="parameter in selectedDevice.parameters" :key="parameter.label">
            <dt>{{ parameter.label }}</dt>
            <dd>{{ parameter.value }}</dd>
          </div>
        </dl>
        <div class="panel-actions">
          <button class="button button-primary" type="button" @click="openDetails(selectedDevice)">展开完整详情</button>
          <button
            class="button button-secondary"
            type="button"
            @click="store.addToDraft(selectedDevice)"
          >
            加入项目方案
          </button>
        </div>
        <EngineerSupplementPanel :product-key="selectedDevice.id" />
      </section>
    </aside>
  </div>

  <p class="engineering-disclaimer">
    正式内容以源创智控经销商管理平台的商品详情和产品说明书为准；网站未提供的参数明确标记为“网站未标注”，
    原Word文件仅保留归档，不参与页面数据生成。
  </p>

  <Teleport to="body">
    <div
      v-if="detailDevice"
      class="terminal-detail-backdrop"
      role="presentation"
      @click.self="closeDetails"
    >
      <section
        class="terminal-detail-dialog"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="`terminal-detail-${detailDevice.id}`"
      >
        <button class="terminal-dialog-close" type="button" aria-label="关闭设备详情" @click="closeDetails">×</button>

        <div class="terminal-dialog-visual">
          <p class="eyebrow">FIELD DEVICE</p>
          <span class="terminal-dialog-image">
            <img
              v-if="coverAsset(detailDevice)"
              :src="store.mediaUrl(coverAsset(detailDevice)!)"
              :alt="coverAsset(detailDevice)!.altText"
            />
          </span>
          <div class="terminal-dialog-highlights">
            <span v-for="item in detailDevice.highlights" :key="item">{{ item }}</span>
          </div>
        </div>

        <div class="terminal-dialog-content">
          <span class="terminal-dialog-type">楼宇控制末端设备</span>
          <h2 :id="`terminal-detail-${detailDevice.id}`">{{ detailDevice.title }}</h2>
          <p>{{ detailDevice.summary }}</p>

          <div class="terminal-model-row">
            <span>产品型号</span><strong>{{ detailDevice.model }}</strong>
          </div>

          <h3>主要参数</h3>
          <dl class="terminal-parameter-grid">
            <div v-for="parameter in detailDevice.parameters" :key="parameter.label">
              <dt>{{ parameter.label }}</dt>
              <dd>{{ parameter.value }}</dd>
            </div>
          </dl>

          <div class="terminal-dialog-actions">
            <button class="button button-primary" type="button" @click="store.addToDraft(detailDevice)">
              加入项目方案
            </button>
            <RouterLink class="button button-secondary" :to="{ name: 'item', params: { id: detailDevice.id } }" @click="closeDetails">
              打开完整详情
            </RouterLink>
            <a
              v-if="manualAsset(detailDevice)"
              class="button button-secondary"
              :href="store.mediaUrl(manualAsset(detailDevice)!)"
              target="_blank"
              rel="noopener"
            >
              查看产品说明书
            </a>
          </div>
        </div>
      </section>
    </div>
  </Teleport>
</template>
